import { PAGE_SIZE_OPTIONS, type SortOrder } from '@chukua/common';
import type { TablePaginationConfig, TableProps } from 'antd';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { parseListParams, toListParams, type ListQuerySchema } from '../policies/list-params';
import { tableChangeToListPatch, type ListPatch, type TableChange } from '../policies/table-change-to-list-patch';

export interface BaseListQuery {
  page: number;
  pageSize: number;
  q?: string;
  sort: string;
  order: SortOrder;
}

export interface SetQueryOptions {
  /** Replace the history entry instead of pushing one — for search-as-you-type, so Back skips the keystrokes. */
  replace?: boolean;
}

/**
 * A list screen's query, kept in the URL. Pair with a `defineListQuery()` schema from
 * @chukua/common and pass the result to the feature's `<feature>.api.ts` list call —
 * see CLAUDE.md → Lists and tables.
 */
export function useListQuery<Q extends BaseListQuery>(schema: ListQuerySchema<Q>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const defaults = useMemo(() => schema.parse({}), [schema]);
  const query = useMemo(() => parseListParams(schema, searchParams), [schema, searchParams]);

  function applyPatch(patch: ListPatch, { replace = false }: SetQueryOptions = {}): void {
    setSearchParams(toListParams({ ...query, ...patch }, defaults), { replace });
  }

  /** Any change other than the page itself goes back to page 1 — page 3 of the old results means nothing for the new ones. */
  function setQuery(patch: Partial<Q>, options?: SetQueryOptions): void {
    applyPatch({ page: 1, ...patch }, options);
  }

  function tableProps<T>(total: number): Pick<TableProps<T>, 'pagination' | 'onChange' | 'sortDirections' | 'showSorterTooltip'> {
    const pagination: TablePaginationConfig = {
      current: query.page,
      pageSize: query.pageSize,
      total,
      showSizeChanger: true,
      pageSizeOptions: [...PAGE_SIZE_OPTIONS],
      showTotal: (count) => `${count} total`,
    };
    return {
      pagination,
      // No "cleared" step: the table always shows which column it is sorted by — so Ant's "Click to cancel sorting" tooltip would be wrong.
      sortDirections: ['ascend', 'descend'],
      showSorterTooltip: false,
      onChange: (changedPagination, filters, sorter, extra) => {
        const change: TableChange = { action: extra.action as TableChange['action'], pagination: changedPagination, filters, sorter };
        applyPatch(tableChangeToListPatch(change, { pageSize: query.pageSize, defaultSort: { sort: defaults.sort, order: defaults.order } }));
      },
    };
  }

  /** Controlled `sortOrder` for a column whose key is a sort field. */
  function sortOrderFor(key: Q['sort']): 'ascend' | 'descend' | null {
    if (query.sort !== key) return null;
    return query.order === 'desc' ? 'descend' : 'ascend';
  }

  /** Controlled `filteredValue` for a column whose key is a `listFilter()` param. */
  function filteredValueFor(key: keyof Q & string): string[] | null {
    const value = query[key];
    return Array.isArray(value) ? value.map(String) : null;
  }

  const isFiltered = Object.entries(query).some(([key, value]) => (key === 'q' ? Boolean(value) : Array.isArray(value) && value.length > 0));

  return { query, setQuery, tableProps, sortOrderFor, filteredValueFor, isFiltered };
}

export type ListQueryControls<Q extends BaseListQuery> = ReturnType<typeof useListQuery<Q>>;
